import React, { useCallback, useEffect, useState } from 'react';
import { backofficeAuth } from '../backofficeFirebaseClient';
import { Search, Filter, X, RefreshCw } from 'lucide-react';

const STALE_MS = 5 * 60 * 1000;

const UsersDirectory = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all'); // all, email, phone, uid
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [lastSyncSource, setLastSyncSource] = useState('listener');
  const [isStale, setIsStale] = useState(false);

  const loadUsers = useCallback(async (source = 'manual_refresh') => {
    try {
      setLoading(true);
      setError('');
      const token = await backofficeAuth.currentUser?.getIdToken?.();
      const resp = await fetch('https://us-central1-acctoo-backoffice.cloudfunctions.net/listUsers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await resp.json();
      setUsers(json?.users || []);
      setFilteredUsers(json?.users || []);
      setLastSyncedAt(new Date().toISOString());
      setLastSyncSource(source);
      setIsStale(false);
    } catch (e) {
      setError(`Firebase: ${e.code || e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers('listener');
  }, [loadUsers]);

  useEffect(() => {
    if (!lastSyncedAt) return undefined;
    const tick = () => {
      const age = Date.now() - new Date(lastSyncedAt).getTime();
      setIsStale(age > STALE_MS);
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [lastSyncedAt]);

  // Filter users based on search term and filter type
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
      return;
    }

    const filtered = users.filter(user => {
      const searchLower = searchTerm.toLowerCase();
      
      switch (filterBy) {
        case 'email':
          return user.email?.toLowerCase().includes(searchLower);
        case 'phone':
          return user.phoneNumber?.toLowerCase().includes(searchLower);
        case 'uid':
          return user.id?.toLowerCase().includes(searchLower);
        case 'all':
        default:
          return (
            user.email?.toLowerCase().includes(searchLower) ||
            user.phoneNumber?.toLowerCase().includes(searchLower) ||
            user.id?.toLowerCase().includes(searchLower)
          );
      }
    });

    setFilteredUsers(filtered);
  }, [searchTerm, filterBy, users]);

  const clearSearch = () => {
    setSearchTerm('');
    setFilterBy('all');
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-xl font-semibold">Users ({filteredUsers.length})</h3>
            {lastSyncedAt && (
              <p className={`text-xs mt-1 ${isStale ? 'text-amber-700' : 'text-gray-500'}`}>
                Last synced: {lastSyncedAt} · source: {lastSyncSource}
                {isStale ? ' · list may be stale — refresh recommended' : ''}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {error && <div className="text-sm text-red-600">{error}</div>}
            <button
              type="button"
              onClick={() => loadUsers('manual_refresh')}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
        
        {/* Search and Filter Controls */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Filter Dropdown */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Fields</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="uid">User ID</option>
              </select>
            </div>
          </div>
          
          {/* Search Results Info */}
          {searchTerm && (
            <div className="text-sm text-gray-600">
              Showing {filteredUsers.length} of {users.length} users
              {filteredUsers.length === 0 && searchTerm && (
                <span className="text-red-600 ml-2">No users found matching your search</span>
              )}
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3">UID</th>
                <th className="text-left py-2 px-3">Email</th>
                <th className="text-left py-2 px-3">Phone</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-3 font-mono">{u.id}</td>
                  <td className="py-2 px-3">{u.email || '-'}</td>
                  <td className="py-2 px-3">{u.phoneNumber || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && <div className="text-sm text-gray-500 mt-3">Loading...</div>}
        </div>
      </div>
    </div>
  );
};

export default UsersDirectory;


